import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import { localizeUserFacingError } from '../../utils/localizedError';
import { skillService } from '../../services/skill';
import { setSkills } from '../../store/slices/skillSlice';
import { RootState } from '../../store';
import { OnlineSkill } from '../../types/skill';
import { getLocalizedOnlineSkillSummary, getLocalizedOnlineSkillTag } from '../../utils/localizedOnlineSkillMetadata';
import OnlineSkillCard from './OnlineSkillCard';
import SkillDetailDialog from './SkillDetailDialog';

type SortOption = 'downloads' | 'stars' | 'updatedAt';

const PAGE_SIZE = 24;

const OnlineSkillsPanel: React.FC = () => {
  const dispatch = useDispatch();
  const installedSkills = useSelector((state: RootState) => state.skill.skills);
  const [skills, setOnlineSkills] = useState<OnlineSkill[]>([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortOption>('downloads');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [installStatus, setInstallStatus] = useState<Record<string, 'idle' | 'installing' | 'installed'>>({});
  const [selectedSkill, setSelectedSkill] = useState<OnlineSkill | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);

  /** 与服务端安装预检保持一致：展示名称可重名、可本地化，不可作为技能身份。 */
  const isAlreadyInstalled = useCallback((slug: string) => installedSkills.some(skill => skill.id === slug), [installedSkills]);

  const fetchSkills = useCallback(async (searchQuery: string, sortBy: SortOption, pageNum: number, append: boolean) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');
    const result = await skillService.fetchOnlineSkills({
      search: searchQuery,
      sort: sortBy,
      page: pageNum,
      limit: PAGE_SIZE,
    });
    if (requestId !== requestIdRef.current) return;
    setLoading(false);
    if (result.success && result.data) {
      if (append) {
        setOnlineSkills(prev => [...prev, ...result.data!.skills]);
      } else {
        setOnlineSkills(result.data.skills);
      }
      setTotal(result.data.total);
      setTotalPages(result.data.totalPages);
    } else {
      setError(localizeUserFacingError(result.error, 'skillsOnlineError'));
    }
  }, []);

  useEffect(() => {
    void fetchSkills('', 'downloads', 1, false);
  }, [fetchSkills]);

  // Web: 语言切换后重置分页并重新拉取（在线技能目录按语言返回不同文案）。
  useEffect(() => i18nService.subscribe(() => {
    setPage(1);
    void fetchSkills('', 'downloads', 1, false);
  }), [fetchSkills]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      fetchSkills(value, sort, 1, false);
    }, 400);
  };

  const handleSortChange = (newSort: SortOption) => {
    if (newSort === sort) return;
    setLoading(true);
    setSort(newSort);
    setPage(1);
    void fetchSkills(search, newSort, 1, false);
  };

  const handleLoadMore = useCallback(() => {
    if (loading || page >= totalPages) return;
    const nextPage = page + 1;
    setPage(nextPage);
    void fetchSkills(search, sort, nextPage, true);
  }, [fetchSkills, loading, page, search, sort, totalPages]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || loading || page >= totalPages) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) handleLoadMore();
    }, { rootMargin: '320px 0px' });
    observer.observe(target);
    return () => observer.disconnect();
  }, [handleLoadMore, loading, page, totalPages]);

  const handleInstall = async (slug: string) => {
    if (installStatus[slug] === 'installing' || installStatus[slug] === 'installed') return;
    if (isAlreadyInstalled(slug)) return;
    setInstallStatus(prev => ({ ...prev, [slug]: 'installing' }));
    const result = await skillService.installOnlineSkill(slug);
    if (result.success) {
      setInstallStatus(prev => ({ ...prev, [slug]: 'installed' }));
      if (result.skills) {
        dispatch(setSkills(result.skills));
      }
    } else {
      setInstallStatus(prev => ({ ...prev, [slug]: 'idle' }));
      setError(localizeUserFacingError(result.error, 'skillDownloadFailed'));
    }
  };

  const sortOptions: { key: SortOption; label: string }[] = [
    { key: 'downloads', label: i18nService.t('skillsOnlineSortDownloads') },
    { key: 'stars', label: i18nService.t('skillsOnlineSortStars') },
    { key: 'updatedAt', label: i18nService.t('skillsOnlineSortUpdated') },
  ];
  const emptyMessageKey = search.trim() ? 'skillsOnlineNoResults' : 'skillsOnlineEmpty';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <p className="max-w-2xl text-[13px] leading-5 text-claude-textSecondary dark:text-claude-darkTextSecondary">
          {i18nService.t('skillsOnlineSubtitle')}
        </p>
        <span className="shrink-0 text-xs tabular-nums text-claude-textSecondary/75 dark:text-claude-darkTextSecondary/75">
          {skills.length} / {total}
        </span>
      </div>

      <div className="flex flex-col gap-2.5 md:flex-row md:items-center">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-claude-textSecondary dark:text-claude-darkTextSecondary" />
          <input
            type="text"
            placeholder={i18nService.t('skillsOnlineSearch')}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-11 w-full rounded-xl border border-claude-border bg-white pl-10 pr-3 text-sm text-claude-text shadow-[0_1px_2px_rgba(0,0,0,0.02)] outline-none transition-[border-color,box-shadow] placeholder:text-claude-textSecondary/70 focus:border-claude-accent/60 focus:ring-4 focus:ring-claude-accent/10 dark:border-claude-darkBorder dark:bg-claude-darkSurface dark:text-claude-darkText dark:placeholder:text-claude-darkTextSecondary/65"
          />
        </div>
        <div className="flex h-11 items-center gap-1 rounded-xl bg-claude-surfaceMuted p-1 dark:bg-claude-darkSurface">
          {sortOptions.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => handleSortChange(opt.key)}
              className={`h-8 rounded-lg px-3 text-xs font-medium transition-all ${
                sort === opt.key
                  ? 'bg-white text-claude-text shadow-sm dark:bg-claude-darkSurfaceHover dark:text-claude-darkText'
                  : 'text-claude-textSecondary hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:text-claude-darkText'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-500 dark:text-red-400 text-center py-2">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {skills.length === 0 && !loading && !error ? (
          <div className="col-span-2 text-center py-8 text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
            {i18nService.t(emptyMessageKey)}
          </div>
        ) : (
          skills.map((skill, index) => (
            <OnlineSkillCard
              key={`${skill.slug}:${index}`}
              skill={skill}
              installStatus={installStatus[skill.slug] || (isAlreadyInstalled(skill.slug) ? 'installed' : 'idle')}
              onInstall={handleInstall}
              onOpenDetails={setSelectedSkill}
            />
          ))
        )}
      </div>

      {loading && (
        <div className="text-center py-4 text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
          {i18nService.t('loading')}
        </div>
      )}

      {skills.length > 0 && page < totalPages && <div ref={loadMoreRef} className="h-px" aria-hidden="true" />}

      <SkillDetailDialog
        item={selectedSkill ? {
          kind: 'online',
          skill: selectedSkill,
          summary: getLocalizedOnlineSkillSummary(selectedSkill),
          tagLabel: selectedSkill.tag ? getLocalizedOnlineSkillTag(selectedSkill.tag) : undefined,
          installStatus: installStatus[selectedSkill.slug]
            || (isAlreadyInstalled(selectedSkill.slug) ? 'installed' : 'idle'),
          onInstall: handleInstall,
        } : null}
        onClose={() => setSelectedSkill(null)}
      />
    </div>
  );
};

export default OnlineSkillsPanel;
